/**
 * Face Cropping Utility
 *
 * Uses face landmark bounding box data to crop photos to focus on the face,
 * removing excess background and ensuring consistent framing for AI processing.
 */

import { logger } from "@/core/logging";

export interface SimpleLandmark {
  x: number;
  y: number;
  z?: number;
}

export interface FaceBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
}

export function getFaceBounds(landmarks: SimpleLandmark[]): FaceBounds {
  let minX = 1;
  let maxX = 0;
  let minY = 1;
  let maxY = 0;

  for (const lm of landmarks) {
    if (lm.x < minX) minX = lm.x;
    if (lm.x > maxX) maxX = lm.x;
    if (lm.y < minY) minY = lm.y;
    if (lm.y > maxY) maxY = lm.y;
  }

  return {
    minX,
    maxX,
    minY,
    maxY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
    width: maxX - minX,
    height: maxY - minY,
  };
}

export function createFaceCropFromImage(
  imageDataUrl: string,
  bounds: FaceBounds,
  padding: number = 0.8,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const imgWidth = img.naturalWidth;
      const imgHeight = img.naturalHeight;

      const faceCenterX = bounds.centerX * imgWidth;
      const faceCenterY = bounds.centerY * imgHeight;
      const faceWidth = bounds.width * imgWidth;
      const faceHeight = bounds.height * imgHeight;

      const faceDimension = Math.max(faceWidth, faceHeight);

      const paddingTop = faceDimension * (padding + 0.4);
      const paddingBottom = faceDimension * (padding + 0.2);
      const paddingSide = faceDimension * padding;

      let cropWidth = faceWidth + paddingSide * 2;
      let cropHeight = faceHeight + paddingTop + paddingBottom;

      const targetAspect = 3 / 4;
      const currentAspect = cropWidth / cropHeight;

      if (currentAspect > targetAspect) {
        cropHeight = cropWidth / targetAspect;
      } else {
        cropWidth = cropHeight * targetAspect;
      }

      const faceVerticalPosition = 0.4;
      let cropX = faceCenterX - cropWidth / 2;
      let cropY = faceCenterY - cropHeight * faceVerticalPosition;

      if (cropX < 0) {
        cropX = 0;
      } else if (cropX + cropWidth > imgWidth) {
        cropX = Math.max(0, imgWidth - cropWidth);
      }

      if (cropY < 0) {
        cropY = 0;
      } else if (cropY + cropHeight > imgHeight) {
        cropY = Math.max(0, imgHeight - cropHeight);
      }

      cropWidth = Math.min(cropWidth, imgWidth - cropX);
      cropHeight = Math.min(cropHeight, imgHeight - cropY);

      const finalAspect = cropWidth / cropHeight;
      if (Math.abs(finalAspect - targetAspect) > 0.05) {
        if (finalAspect > targetAspect) {
          const newWidth = cropHeight * targetAspect;
          cropX += (cropWidth - newWidth) / 2;
          cropWidth = newWidth;
        } else {
          cropHeight = cropWidth / targetAspect;
        }
      }

      const canvas = document.createElement("canvas");
      // Cap the long side at 1024 to match the backend analysis input size, so a
      // sharp capture is not downscaled below what the server can consume.
      const outputWidth = Math.min(1024, cropWidth);
      const outputHeight = outputWidth / targetAspect;
      canvas.width = outputWidth;
      canvas.height = outputHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, outputWidth, outputHeight);

      const croppedDataUrl = canvas.toDataURL("image/jpeg", 0.92);
      resolve(croppedDataUrl);
    };

    img.onerror = () => {
      reject(new Error("Failed to load image for cropping"));
    };

    img.src = imageDataUrl;
  });
}

export async function createFaceCropWithFallback(
  imageDataUrl: string,
  landmarks: SimpleLandmark[] | null,
): Promise<string> {
  if (!landmarks || landmarks.length === 0) {
    return imageDataUrl;
  }

  try {
    const bounds = getFaceBounds(landmarks);
    if (bounds.width < 0.1 || bounds.height < 0.1) {
      return imageDataUrl;
    }
    return await createFaceCropFromImage(imageDataUrl, bounds);
  } catch (error) {
    logger.error("face_crop_failed_using_original", undefined, error as Error);
    return imageDataUrl;
  }
}

// MediaPipe FaceMesh landmark indices that bound the eye + eyebrow band for the
// pinned `face_landmarker float16/1` model. We deliberately use a narrow subset
// rather than all 478 points so the resulting crop is just the eyes-and-brows
// region, not the whole face. Indices come from the canonical FaceMesh topology
// (Google MediaPipe). If the model URL in `mediapipeConfig.ts` ever changes,
// re-verify these against the new topology.
//
// Coverage:
//  - Eyebrows (upper edge of crop): left arch 70/63/105/66/107, right 336/296/334/293/300
//  - Eye horizontal extremes (left/right of crop): outer/inner canthi 33/133/362/263, plus 130/359
//  - Lower lash line (lower edge of crop): under-eye 145, 374
export const EYE_REGION_LANDMARK_INDICES: readonly number[] = [
  70, 63, 105, 66, 107, 336, 296, 334, 293, 300, 33, 133, 362, 263, 130, 359, 145, 374,
] as const;

// FaceMesh landmark indices that sit on the facial midline near the eye band.
// Used to anchor the crop center on the nasion / brow midline rather than the
// midpoint of the detected eye-region bounds, which can drift sideways when the
// head is even slightly off-center in the frame and produce a lopsided crop.
const EYE_REGION_MIDLINE_ANCHOR_INDICES: readonly number[] = [6, 168, 8] as const;

export function getEyeRegionBounds(landmarks: SimpleLandmark[]): FaceBounds | null {
  let minX = 1;
  let maxX = 0;
  let minY = 1;
  let maxY = 0;
  let hadAny = false;

  for (const idx of EYE_REGION_LANDMARK_INDICES) {
    const lm = landmarks[idx];
    if (!lm) continue;
    hadAny = true;
    if (lm.x < minX) minX = lm.x;
    if (lm.x > maxX) maxX = lm.x;
    if (lm.y < minY) minY = lm.y;
    if (lm.y > maxY) maxY = lm.y;
  }

  if (!hadAny) return null;

  let centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  for (const idx of EYE_REGION_MIDLINE_ANCHOR_INDICES) {
    const anchor = landmarks[idx];
    // Only adopt the anchor when it actually sits inside the detected eye-band
    // bounds. This protects against zero-initialised meshes and against the
    // anchor being clobbered when the head is rotated away from the camera.
    if (anchor && anchor.x >= minX && anchor.x <= maxX) {
      centerX = anchor.x;
      break;
    }
  }

  return {
    minX,
    maxX,
    minY,
    maxY,
    centerX,
    centerY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

// Outer canthi — the two landmarks useFaceLandmarker measures for `faceSize`.
const FACE_SIZE_LEFT_CANTHUS_INDEX = 33;
const FACE_SIZE_RIGHT_CANTHUS_INDEX = 263;

// Outer-canthi span (fraction of frame width) a face needs before the lash & brow eye
// crop is cut; below it the frontal photo itself goes to the validator. Stricter than
// the live guide's IDEAL_FACE_SIZE_MIN (0.045) on purpose: half-body demo photos filling
// the frame measure <= 0.17 and the loosest face demo 0.28, so 0.2 keeps upscaled eye
// crops of waist-up shots out while every genuine face or eye shot still crops.
export const LASH_BROW_EYE_CROP_MIN_FACE_SIZE = 0.2;

export function getFaceSize(landmarks: SimpleLandmark[]): number {
  const left = landmarks[FACE_SIZE_LEFT_CANTHUS_INDEX];
  const right = landmarks[FACE_SIZE_RIGHT_CANTHUS_INDEX];
  if (!left || !right) {
    return 0;
  }
  return Math.hypot(right.x - left.x, right.y - left.y);
}

export interface EyeRegionCropOptions {
  /** Aspect ratio (width / height) for the output crop. Eyes-and-brows is a
   *  landscape band, so the default is 16:9. */
  targetAspect?: number;
  /** Horizontal padding past the outer canthi, expressed as a fraction of the
   *  detected eye-region width. */
  horizontalPadding?: number;
  /** Padding above the brows, expressed as a fraction of the detected
   *  eye-region height. */
  topPadding?: number;
  /** Padding below the lower lash line, expressed as a fraction of the detected
   *  eye-region height. */
  bottomPadding?: number;
  /** Maximum output width in pixels. */
  maxOutputWidth?: number;
}

// Padding tuned so a typical eye-region detection produces a 16:9 crop with
// both eyes well inside the frame (lateral context past the outer canthi,
// forehead above the brows, cheek below the lashes) instead of a tight zoom.
const DEFAULT_EYE_REGION_OPTIONS: Required<EyeRegionCropOptions> = {
  targetAspect: 16 / 9,
  horizontalPadding: 0.5,
  topPadding: 1.2,
  bottomPadding: 1.0,
  maxOutputWidth: 960,
};

export function createEyeRegionCropFromImage(
  imageDataUrl: string,
  bounds: FaceBounds,
  options: EyeRegionCropOptions = {},
): Promise<string> {
  const { targetAspect, horizontalPadding, topPadding, bottomPadding, maxOutputWidth } = {
    ...DEFAULT_EYE_REGION_OPTIONS,
    ...options,
  };

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const imgWidth = img.naturalWidth;
      const imgHeight = img.naturalHeight;

      const regionCenterX = bounds.centerX * imgWidth;
      const regionCenterY = bounds.centerY * imgHeight;
      const regionWidth = bounds.width * imgWidth;
      const regionHeight = bounds.height * imgHeight;

      const paddedWidth = regionWidth * (1 + horizontalPadding * 2);
      const paddedHeight = regionHeight * (1 + topPadding + bottomPadding);

      let cropWidth = paddedWidth;
      let cropHeight = paddedHeight;
      const currentAspect = cropWidth / cropHeight;

      // Expand the shorter axis so both eyes and the full brow band stay in
      // frame at the requested landscape aspect.
      if (currentAspect > targetAspect) {
        cropHeight = cropWidth / targetAspect;
      } else {
        cropWidth = cropHeight * targetAspect;
      }

      let cropX = regionCenterX - cropWidth / 2;
      let cropY = regionCenterY - cropHeight / 2;

      if (cropX < 0) {
        cropX = 0;
      } else if (cropX + cropWidth > imgWidth) {
        cropX = Math.max(0, imgWidth - cropWidth);
      }

      if (cropY < 0) {
        cropY = 0;
      } else if (cropY + cropHeight > imgHeight) {
        cropY = Math.max(0, imgHeight - cropHeight);
      }

      cropWidth = Math.min(cropWidth, imgWidth - cropX);
      cropHeight = Math.min(cropHeight, imgHeight - cropY);

      // Re-snap to target aspect after edge-clamping so the crop never goes
      // taller than expected when the user is near the top of the frame.
      const finalAspect = cropWidth / cropHeight;
      if (Math.abs(finalAspect - targetAspect) > 0.05) {
        if (finalAspect > targetAspect) {
          const newWidth = cropHeight * targetAspect;
          cropX += (cropWidth - newWidth) / 2;
          cropWidth = newWidth;
        } else {
          const newHeight = cropWidth / targetAspect;
          cropY += (cropHeight - newHeight) / 2;
          cropHeight = newHeight;
        }
      }

      const canvas = document.createElement("canvas");
      const outputWidth = Math.min(maxOutputWidth, cropWidth);
      const outputHeight = outputWidth / targetAspect;
      canvas.width = outputWidth;
      canvas.height = outputHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, outputWidth, outputHeight);

      const croppedDataUrl = canvas.toDataURL("image/jpeg", 0.92);
      resolve(croppedDataUrl);
    };

    img.onerror = () => {
      reject(new Error("Failed to load image for eye-region cropping"));
    };

    img.src = imageDataUrl;
  });
}

// Never substitutes the raw frame for a missing crop: LashBrowCaptureStage uploads
// the result as the eye crop, and the server validates and analyzes it as one.
export async function createEyeRegionCrop(
  imageDataUrl: string,
  landmarks: SimpleLandmark[] | null,
  options: EyeRegionCropOptions = {},
): Promise<string | null> {
  if (!landmarks || landmarks.length === 0) {
    return null;
  }

  try {
    const bounds = getEyeRegionBounds(landmarks);
    if (!bounds) {
      return null;
    }
    // A waist-up or arm's-length face the live guide still accepts would be upscaled
    // into a crop that reads as an eye close-up; leave it out so the server judges
    // the photo itself. Same measure as useFaceLandmarker, stricter threshold.
    if (getFaceSize(landmarks) < LASH_BROW_EYE_CROP_MIN_FACE_SIZE) {
      return null;
    }
    // Guard against degenerate bounds from partial detection or lens occlusion
    // before producing a crop that would just be background.
    if (bounds.height < 0.01) {
      return null;
    }
    return await createEyeRegionCropFromImage(imageDataUrl, bounds, options);
  } catch (error) {
    logger.error("eye_region_crop_failed", undefined, error as Error);
    return null;
  }
}
